import React, { useEffect, useState, Children, useMemo } from 'react'
import PropTypes from 'prop-types'
import orderBy from 'lodash/orderBy'
import capitalize from 'lodash/capitalize'

import { Box, Pagination } from '@mui/material'

import { get } from 'utils/lodash'
import { handleError } from 'utils/error'
import { CUSTOMER_DETAILS_DEFAULT_PAGINATION } from 'settings/constants/pagination'
import { PAYMENT_STATUS } from 'settings/constants/payment'
import { useLazyGetAccountPaymentsQuery } from 'api/accounts/getAccountPayments'
import { useLazyGetEmailEventsQuery } from 'api/accounts/getEmailEvents'
import { getPaymentReceiptEmailEvents } from 'data/email/emailEventsSelectors'

import T from 'T'
import HHAccordion from 'components/common/HHAccordion'
import EventBlocker from 'components/common/EventBlocker'
import HHTablePagination from 'components/common/HHTablePagination'
import PaymentsActionMenu from 'components/billing/common/PaymentsActionMenu'
import TakePaymentButton from 'components/common/payments/TakePaymentButton'
import HHSectionPlaceholder from 'components/common/HHSectionPlaceholder'
import PaymentReceiptDialog from 'components/billing/payments/PaymentReceiptDialog'

import PaymentAccordion from '../content/payments/PaymentAccordion'
import PaymentNoteDialog from '../content/payments/PaymentNoteDialog'

const { DECLINED, REVERSED, VOIDED, REFUNDED, TRANSFERRED } = PAYMENT_STATUS
const { INITIAL_PAGE, ROWS_PER_PAGE, ROWS_PER_PAGE_OPTIONS } = CUSTOMER_DETAILS_DEFAULT_PAGINATION

const PaymentsFortis = ({ accountId, customerDetails = null, showTakePaymentButton = false, isViewOnly = false }) => {
  const [getAccountPayments, { data: paymentList, isFetching: isLoading }] = useLazyGetAccountPaymentsQuery()
  const [getEmailEvents, { data: emailEventList }] = useLazyGetEmailEventsQuery()
  const [pagination, setPagination] = useState({ page: INITIAL_PAGE, rowsPerPage: ROWS_PER_PAGE })
  const [selectedPayment, setSelectedPayment] = useState(null)
  const [paymentsAnchorEl, setPaymentsAnchorEl] = useState(null)
  const [paymentNoteDialog, setPaymentNoteDialog] = useState({ isOpen: false, note: '' })
  const [paymentReceiptDialog, setPaymentReceiptDialog] = useState({ isOpen: false, payment: null })

  const { page, rowsPerPage } = pagination
  const shimmersArray = new Array(1).fill(0)

  const handlePageChange = (newPage = INITIAL_PAGE) => setPagination(p => ({ ...p, page: newPage }))

  const handleOpenActionMenu = event => {
    event.stopPropagation()
    setPaymentsAnchorEl(event.currentTarget)
  }

  const handleCloseActionMenu = () => {
    setPaymentsAnchorEl(null)
    setSelectedPayment(null)
  }

  const handleOpenPaymentReceiptDrawer = payment => {
    if (!payment?.paymentNumber || !payment?.id) return
    setPaymentReceiptDialog({ isOpen: true, payment })
  }

  const handleClosePaymentReceiptDrawer = () => setPaymentReceiptDialog({ isOpen: false, payment: null })

  const allPayments = useMemo(() => orderBy(get(paymentList, 'payments', []), ['transactionPostedDate'], ['desc']), [paymentList])
  const emailEvents = useMemo(() => getPaymentReceiptEmailEvents({ emailEvents: get(emailEventList, 'emailEvents', []) }), [emailEventList])

  const consolidatedPayments = useMemo(() => {
    const grouped = allPayments.reduce((acc, payment) => {
      const key = payment.paymentNumber
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(payment)
      return acc
    }, {})

    const consolidated = Object.values(grouped).map(paymentGroup => {
      const history = orderBy(paymentGroup, ['transactionPostedDate'], ['desc'])
      const currentPayment = history[0]

      // Original payment is the oldest record in the group
      const originalPayment = history[history.length - 1]
      const refundedAmountCents = originalPayment.refundedAmountCents || 0
      const hasPartialRefunds = refundedAmountCents > 0
      const isFullyRefundedByPartials = refundedAmountCents >= originalPayment.transactionAmountCents && hasPartialRefunds
      // For full refunds/reversals, the most recent ATH status reflects the reversal
      // Backend sends REFUNDED for gateway refunds and REVERSED for internal reversals
      const currentStatus = capitalize(currentPayment.transactionStatus)
      const isPaymentReversed = !hasPartialRefunds && (currentStatus === REVERSED || currentStatus === VOIDED || currentStatus === REFUNDED || currentStatus === TRANSFERRED)
      const isFullyRefunded = isFullyRefundedByPartials || isPaymentReversed

      const displayStatus = isFullyRefunded ? currentStatus : originalPayment.transactionStatus

      return {
        ...currentPayment,
        // Use the original payment's amount and status for action menu calculations
        transactionAmountCents: originalPayment.transactionAmountCents,
        transactionStatus: displayStatus,
        // Payment method details from original payment (refund ATH records don't carry these)
        creditCardType: originalPayment.creditCardType,
        accountLastFour: originalPayment.accountLastFour,
        // refundedAmountCents comes from APH via the API (DB source of truth)
        refundedAmountCents,
        history,
        historyCount: history.length,
      }
    })

    return orderBy(consolidated, ['transactionPostedDate'], ['desc'])
  }, [allPayments])

  const currentPayments = isLoading ? shimmersArray : consolidatedPayments.slice(page * rowsPerPage, (page + 1) * rowsPerPage)

  useEffect(() => {
    if (accountId) {
      getAccountPayments({ accountId }).unwrap().catch(handleError)
      getEmailEvents({ accountId }).unwrap().catch(handleError)
    }
  }, [accountId])

  return (
    <>
      <HHAccordion
        withNewColors
        isExpanded
        isExpandCollapseHeightSame
        summary={T.PAYMENTS}
        showActionIcon={false}
        showCustomActions={showTakePaymentButton}
        customActionContent={
          <EventBlocker preventDefault={false}>
            <TakePaymentButton accountId={accountId} />
          </EventBlocker>
        }
      >
        <Box width="100%">
          <Box borderBottom="1px solid" borderColor="border.light">
            {Children.toArray(
              currentPayments.map(payment => (
                <PaymentAccordion
                  isViewOnly={isViewOnly}
                  isLoading={isLoading}
                  accountNumber={get(customerDetails, 'accountNumber')}
                  payment={payment}
                  emailEvents={emailEvents.filter(event => event?.paymentNumber === payment?.paymentNumber)}
                  hasActivity={capitalize(payment?.transactionStatus) !== DECLINED}
                  onPaymentNoteClick={note => setPaymentNoteDialog({ isOpen: true, note })}
                  onActionClick={event => {
                    handleOpenActionMenu(event)
                    setSelectedPayment(payment)
                  }}
                  onClick={() => handleOpenPaymentReceiptDrawer(payment)}
                />
              ))
            )}
          </Box>

          {consolidatedPayments.length > 0 && (
            <Box backgroundColor="background.header" px={3} display="flex" alignItems="center" justifyContent="space-between">
              <HHTablePagination
                page={page}
                component="div"
                count={consolidatedPayments.length}
                rowsPerPage={rowsPerPage}
                onPageChange={(event, newPage) => handlePageChange(newPage)}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                onRowsPerPageChange={event => setPagination({ page: INITIAL_PAGE, rowsPerPage: event.target.value })}
                ActionsComponent={params => <Box display="none" {...params} />}
              />
              <Pagination
                color="primary"
                count={Math.ceil(consolidatedPayments.length / rowsPerPage)}
                page={page + 1}
                onChange={(event, newPage) => handlePageChange(newPage - 1)}
              />
            </Box>
          )}

          {!isLoading && consolidatedPayments.length === 0 && <HHSectionPlaceholder title={T.NO_PAYMENTS} />}
        </Box>
      </HHAccordion>

      <PaymentsActionMenu
        anchorEl={paymentsAnchorEl}
        customerDetails={customerDetails}
        selectedPayment={selectedPayment}
        onClose={handleCloseActionMenu}
      />
      <PaymentNoteDialog
        isOpen={paymentNoteDialog.isOpen}
        note={paymentNoteDialog.note}
        onClose={() => setPaymentNoteDialog({ isOpen: false, note: '' })}
      />
      <PaymentReceiptDialog
        open={paymentReceiptDialog.isOpen}
        accountId={accountId}
        accountName={customerDetails?.accountName}
        paymentNumber={paymentReceiptDialog?.payment?.paymentNumber}
        accountTransactionHistoryId={paymentReceiptDialog?.payment?.id}
        onClose={handleClosePaymentReceiptDrawer}
      />
    </>
  )
}

PaymentsFortis.propTypes = {
  accountId: PropTypes.string.isRequired,
  customerDetails: PropTypes.object,
  showTakePaymentButton: PropTypes.bool,
  isViewOnly: PropTypes.bool,
}

export default PaymentsFortis
